const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem('wallness_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Progress API
export const progressApi = {
  getDashboard: async () => {
    const response = await fetch(`${API_URL}/api/progress/dashboard`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener dashboard');
    return response.json();
  },

  getStats: async () => {
    const response = await fetch(`${API_URL}/api/progress/stats`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener estadísticas');
    return response.json();
  },

  logHydration: async (amountMl) => {
    const response = await fetch(`${API_URL}/api/progress/hydration`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ amount_ml: amountMl })
    });
    if (!response.ok) throw new Error('Error al registrar hidratación');
    return response.json();
  },

  getTodayHydration: async () => {
    const response = await fetch(`${API_URL}/api/progress/hydration/today`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener hidratación');
    return response.json();
  },

  getHistory: async (period = '7d') => {
    const response = await fetch(`${API_URL}/api/progress/history?period=${period}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener historial');
    return response.json();
  }
};

// Fitness API
export const fitnessApi = {
  getPrograms: async (category = null) => {
    const url = category 
      ? `${API_URL}/api/fitness/programs?category=${category}`
      : `${API_URL}/api/fitness/programs`;
    const response = await fetch(url, { headers: getHeaders() });
    if (!response.ok) throw new Error('Error al obtener programas');
    return response.json();
  },

  getProgram: async (programId) => {
    const response = await fetch(`${API_URL}/api/fitness/programs/${programId}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener programa');
    return response.json();
  },

  getProgramWorkouts: async (programId) => {
    const response = await fetch(`${API_URL}/api/fitness/programs/${programId}/workouts`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener entrenamientos');
    return response.json();
  },

  getWorkout: async (workoutId) => {
    const response = await fetch(`${API_URL}/api/fitness/workouts/${workoutId}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener entrenamiento');
    return response.json();
  },

  completeWorkout: async (workoutId, durationMinutes, caloriesBurned = null) => {
    const response = await fetch(`${API_URL}/api/fitness/workouts/${workoutId}/complete`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ 
        duration_minutes: durationMinutes,
        calories_burned: caloriesBurned
      })
    });
    if (!response.ok) throw new Error('Error al completar entrenamiento');
    return response.json();
  },

  getMyCompletions: async (days = 7) => {
    const response = await fetch(`${API_URL}/api/fitness/my-completions?days=${days}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener completados');
    return response.json();
  }
};

// Wellbeing API
export const wellbeingApi = {
  // Mood
  logMood: async (mood, note = null) => {
    const response = await fetch(`${API_URL}/api/wellbeing/mood`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ mood, note })
    });
    if (!response.ok) throw new Error('Error al registrar estado de ánimo');
    return response.json();
  },

  getMoodHistory: async (days = 7) => {
    const response = await fetch(`${API_URL}/api/wellbeing/mood/history?days=${days}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener historial');
    return response.json();
  },

  // Sleep
  logSleep: async (data) => {
    const response = await fetch(`${API_URL}/api/wellbeing/sleep`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Error al registrar sueño');
    return response.json();
  },

  getSleepHistory: async (days = 7) => {
    const response = await fetch(`${API_URL}/api/wellbeing/sleep/history?days=${days}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener historial de sueño');
    return response.json();
  },

  getTodaySleep: async () => {
    const response = await fetch(`${API_URL}/api/wellbeing/sleep/today`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener sueño de hoy');
    return response.json();
  },

  // Meditations
  getMeditations: async (category = null) => {
    const url = category 
      ? `${API_URL}/api/wellbeing/meditations?category=${category}`
      : `${API_URL}/api/wellbeing/meditations`;
    const response = await fetch(url, { headers: getHeaders() });
    if (!response.ok) throw new Error('Error al obtener meditaciones');
    return response.json();
  },

  getMeditation: async (id) => {
    const response = await fetch(`${API_URL}/api/wellbeing/meditations/${id}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener meditación');
    return response.json();
  },

  completeMeditation: async (meditationId, data) => {
    const response = await fetch(`${API_URL}/api/wellbeing/meditations/${meditationId}/complete`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Error al completar meditación');
    return response.json();
  },

  getMeditationHistory: async (days = 7) => {
    const response = await fetch(`${API_URL}/api/wellbeing/meditations/sessions/history?days=${days}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener historial de meditación');
    return response.json();
  },

  // Dashboard
  getDashboard: async () => {
    const response = await fetch(`${API_URL}/api/wellbeing/dashboard`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener dashboard de bienestar');
    return response.json();
  }
};

// Nutrition API
export const nutritionApi = {
  getFoods: async (category = null, search = null) => {
    let url = `${API_URL}/api/nutrition/foods`;
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await fetch(url, { headers: getHeaders() });
    if (!response.ok) throw new Error('Error al obtener alimentos');
    return response.json();
  },

  logMeal: async (data) => {
    const response = await fetch(`${API_URL}/api/nutrition/meals`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Error al registrar comida');
    return response.json();
  },

  getTodayMeals: async () => {
    const response = await fetch(`${API_URL}/api/nutrition/meals/today`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener comidas de hoy');
    return response.json();
  },

  getMealHistory: async (days = 7) => {
    const response = await fetch(`${API_URL}/api/nutrition/meals/history?days=${days}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener historial');
    return response.json();
  },

  deleteMeal: async (mealId) => {
    const response = await fetch(`${API_URL}/api/nutrition/meals/${mealId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al eliminar comida');
    return response.json();
  },

  getRecipes: async (category = null) => {
    const url = category 
      ? `${API_URL}/api/nutrition/recipes?category=${category}`
      : `${API_URL}/api/nutrition/recipes`;
    const response = await fetch(url, { headers: getHeaders() });
    if (!response.ok) throw new Error('Error al obtener recetas');
    return response.json();
  },

  getRecipe: async (id) => {
    const response = await fetch(`${API_URL}/api/nutrition/recipes/${id}`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener receta');
    return response.json();
  },

  getDashboard: async () => {
    const response = await fetch(`${API_URL}/api/nutrition/dashboard`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener dashboard de nutrición');
    return response.json();
  }
};

// Achievements API
export const achievementsApi = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/achievements`, {
      headers: getHeaders()
    });
    if (!response.ok) throw new Error('Error al obtener logros');
    return response.json();
  }
};
